/**
 * THE GROUP-CONSOLIDATED VIEW — v19 D3 consolidated, THE ONE HARD RULE, at
 * `/countries/:cc/group` (a GLOBAL surface — the API route carries no `:cc`).
 *
 * ===========================================================================
 * NEVER A CONFIDENT NUMBER FROM AN UNVERIFIED RATE — ON ANY CHANNEL
 * ===========================================================================
 * The server answers one of two shapes and this screen has one of two bodies:
 *   · `fx.status === 'unverified'` → `consolidated` is null. The screen renders the
 *     FX GATE PANEL (`FxGatePanel.tsx`): which pairs, since when, who can clear them — and
 *     NO figure, NO tile, NO "Export (INR)" control. The export control is rendered only
 *     from a `consolidated` object, so in this state the element does not exist in the DOM
 *     (asserted by `group-dashboard.test.tsx`, and falsified there both directions).
 *   · `fx.status === 'verified'` → the consolidated INR tiles render, with the rate TABLE
 *     (which pair, which book period, stale or not — never the rate) and the export.
 *
 * STALE-BUT-VERIFIED, ON THE SCREEN (owner ruling #2, 2026-08-23): "the staleness must be
 * visible on the SCREEN, not only as stale:true on the wire. Last month's rate producing
 * this month's figure is acceptable; producing it silently is not." The marker renders in
 * TWO places, both inside `ConsolidatedCard`, and deliberately NOT in `FxGatePanel`:
 *   · the STALE-RATES NOTICE (`data-testid="stale-rates"`), between the card header and
 *     the tiles — the reader meets it before the figures it qualifies. It names the pair
 *     and the rate's period IN WORDS ("AED→INR uses the Jul 2026 rate (2026-07)…") — not
 *     tooltip-only, not colour-only (WCAG 2.x SC 1.4.1 Use of Color: colour may reinforce
 *     but never solely carry information; the warn pill is reinforcement, the words are
 *     the carrier). Driven by `consolidated.rate_table`, which the server filters to the
 *     currencies that actually CONVERTED a figure on this screen (`usedCurrencies` in
 *     `group-consolidation.ts`) — so the notice can neither fire for a rate that touched
 *     no figure nor stay silent for one that did.
 *   · the "Rates used" line names each stale pair the same way, so the card's provenance
 *     sentence stays honest on its own.
 * `FxGatePanel` is unchanged BY DECISION: in the unverified state no converted figure
 * renders at all (that is the panel's whole design), so ruling #2's trigger — a figure
 * read through a stale rate — cannot occur there; per-pair staleness is already text in
 * its table (the "Book period used" column + "carried from an earlier period").
 * The per-country blocks render in BOTH states: they are in each country's own currency
 * and never touched a rate.
 *
 * GATE: `dashboard.group` — seeded GRP_ONLY, reachable by `grp:true` or an explicit
 * per-user override. The refusal is the designed D5 Alert and never names the key. The
 * `consolidated.total_inr` figures carry no `country_code`, so for a non-grp override seat
 * the interceptor masks them (UNKNOWN masks) — `ServerAmount` renders the marker and the
 * export carries the marker. Export buttons are `reports.export`, like every export.
 *
 * The window is resolved in HQ's calendar (`Asia/Kolkata`, the FX book's own zone) and the
 * `resolved-range` sentence says so; the country dashboards use each country's own zone.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';

import { useCountryCode } from '../../auth/guards';
import { useSession } from '../../auth/session';
import { PageHeader } from '../../components/shell/AppShell';
import { Alert, SkeletonBlock } from '../../components/ui';
import { COUNTRIES } from '../../domain/countries';
import { isCountryCode } from '../../domain/countries';
import { downloadCsv, rowsToCsv } from '../../lib/csv';
import { formatMoney } from '../inquiries/format';
import { ServerAmount } from '../quotes/money';
import { dashboardErrorText, dashboardKeys, fetchGroupDashboard } from './api';
import type { ConsolidatedDto, GroupCountryBlockDto, GroupDashboardDto } from './api';
import { KpiTile } from './dataviz';
import { FxGatePanel } from './FxGatePanel';
import { quoteStageFacts } from './labels';
import { PeriodFilter } from './PeriodFilter';
import { readPeriodQuery } from './periods';

export function GroupDashboardPage(): React.JSX.Element {
  const countryCode = useCountryCode();
  const { isGroupAdmin, accessibleCountries, can } = useSession();
  // `dashboard.group` is group-wide by name: held anywhere is held. `grp:true` holds all.
  const canView = isGroupAdmin || accessibleCountries.some((code) => can(code, 'dashboard.group'));
  const canExport = isGroupAdmin || accessibleCountries.some((code) => can(code, 'reports.export'));

  const [params] = useSearchParams();
  const period = useMemo(() => readPeriodQuery(params), [params]);

  const query = useQuery({
    queryKey: dashboardKeys.group(period),
    queryFn: ({ signal }) => fetchGroupDashboard(period, signal),
    enabled: canView,
  });

  if (!canView) {
    return (
      <div className="flex flex-col gap-4 p-6">
        <Alert tone="danger" title="The group dashboard is not available to this account">
          The consolidated view needs group-wide access. Ask a group administrator to widen
          your access.
        </Alert>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 p-6" data-testid="group-dashboard" data-standing-in={countryCode}>
      <PageHeader
        title="Group dashboard"
        subtitle="Every active country · consolidated in INR only when every exchange rate is verified"
      />

      <PeriodFilter id="group" resolved={query.data?.window} />

      {query.isPending ? (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4" aria-busy="true" aria-label="Loading the group dashboard">
          <SkeletonBlock className="h-[92px]" />
          <SkeletonBlock className="h-[92px]" />
          <SkeletonBlock className="h-[92px]" />
          <SkeletonBlock className="h-[92px]" />
        </div>
      ) : query.isError ? (
        <Alert tone="danger" title="The group dashboard could not be loaded">
          <p>{dashboardErrorText(query.error)}</p>
          <button
            type="button"
            onClick={() => {
              void query.refetch();
            }}
            className="mt-1 font-medium underline underline-offset-2"
          >
            Try again.
          </button>
        </Alert>
      ) : (
        <GroupBody data={query.data} canExport={canExport} />
      )}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────── body ── */

function GroupBody({ data, canExport }: { data: GroupDashboardDto; canExport: boolean }): React.JSX.Element {
  /*
   * ⚠ THE GATE, IN ONE PLACE. `consolidated` is rendered ONLY when the server says the
   * rates are verified AND handed an object. Both halves are checked: a `consolidated`
   * that somehow arrived beside `status: 'unverified'` would still not render — the
   * status is the product's word and the object is not trusted over it.
   */
  const verified = data.fx.status === 'verified' && data.consolidated !== null;
  return (
    <div className="flex flex-col gap-6">
      {verified ? (
        <ConsolidatedCard consolidated={data.consolidated as ConsolidatedDto} period={data.fx.period} canExport={canExport} />
      ) : (
        <FxGatePanel fx={data.fx} />
      )}
      <CountriesTable countries={data.countries} canExport={canExport} />
    </div>
  );
}

/* ─────────────────────────────────────────────────────── consolidated ── */

/** '2026-07' → 'Jul 2026' (UTC, so the book month is the shown month). Wire-shape guard: anything else passes through. */
function monthLabel(period: string | null): string {
  if (period === null || !/^\d{4}-\d{2}$/.test(period)) return period ?? '—';
  return new Intl.DateTimeFormat('en-GB', { month: 'short', year: 'numeric', timeZone: 'UTC' }).format(
    new Date(`${period}-01T00:00:00.000Z`),
  );
}

function ConsolidatedCard({ consolidated, period, canExport }: { consolidated: ConsolidatedDto; period: string; canExport: boolean }): React.JSX.Element {
  const headers = ['Stage', 'Quotes', 'Total (INR)'] as const;
  const rows = consolidated.by_stage.map((entry) => [
    quoteStageFacts(entry.stage).label,
    String(entry.count),
    // The VISIBLE string — a masked seat exports the marker, never a digit it was refused.
    formatMoney(entry.total_inr, 'INR'),
  ]);
  rows.push(['Work in flight', String(consolidated.in_flight.count), formatMoney(consolidated.in_flight.total_inr, 'INR')]);

  // Owner ruling #2: a figure from a carried-forward rate is ALLOWED — but never silent.
  const staleUses = consolidated.rate_table.filter((use) => use.stale);

  return (
    <section aria-labelledby="consolidated-title" data-testid="consolidated" className="flex flex-col gap-3 rounded-md border border-line bg-surface p-5">
      <header className="flex flex-wrap items-baseline justify-between gap-3">
        <h2 id="consolidated-title" className="text-h3 text-fg">Consolidated — INR</h2>
        {canExport ? (
          <button
            type="button"
            onClick={() => {
              downloadCsv('group-consolidated-inr.csv', rowsToCsv(headers, rows));
            }}
            className="flex h-8 items-center gap-1.5 whitespace-nowrap rounded-sm border border-line bg-surface px-2.5 text-body13 font-medium text-fg hover:bg-subtle"
          >
            Export (INR)
          </button>
        ) : null}
      </header>
      {/*
       * THE STALE-RATES NOTICE — ruling #2, WHERE THE FIGURE IS READ. Rendered between the
       * header and the tiles so no INR figure below can be read without passing it. The
       * information is WORDS naming pair + period (WCAG SC 1.4.1: never colour-only), in
       * normal document flow (never tooltip-only). Fresh book ⇒ this block does not exist.
       */}
      {staleUses.length === 0 ? null : (
        <div data-testid="stale-rates" className="flex flex-col gap-1.5 rounded-md border border-warn-fg bg-surface p-3">
          <span className="inline-flex w-fit items-center rounded-pill bg-warn-bg px-2.5 py-1 text-label font-medium text-warn-fg">
            {staleUses.length === 1 ? 'Stale rate in use' : 'Stale rates in use'}
          </span>
          {staleUses.map((use) => (
            <p key={use.from_ccy} className="text-body13 text-fg">
              <span className="font-mono text-mono">{use.from_ccy}→INR</span> uses the{' '}
              <span className="font-medium">{monthLabel(use.period_used)}</span> rate{' '}
              <span className="tnum">({use.period_used ?? '—'})</span>, carried forward — the{' '}
              <span className="tnum">{period}</span> book has no verified {use.from_ccy} rate of its own.
            </p>
          ))}
        </div>
      )}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        {consolidated.by_stage.map((entry) => (
          <KpiTile
            key={entry.stage}
            testId={`inr-${entry.stage}`}
            label={quoteStageFacts(entry.stage).label}
            value={<ServerAmount value={entry.total_inr} currency="INR" />}
            detail={`${String(entry.count)} ${entry.count === 1 ? 'quote' : 'quotes'}`}
          />
        ))}
        <KpiTile
          testId="inr-in-flight"
          label="Work in flight"
          value={<ServerAmount value={consolidated.in_flight.total_inr} currency="INR" />}
          detail={`${String(consolidated.in_flight.count)} ${consolidated.in_flight.count === 1 ? 'document' : 'documents'}`}
        />
      </div>
      {/* WHICH rates, WHICH book period — never the rate. A stale pair is NAMED the same
          way the notice above names it (ruling #2 — the provenance line stays honest alone). */}
      <p className="text-body13 text-fg-secondary" data-testid="rate-table">
        Rates used:{' '}
        {consolidated.rate_table.length === 0
          ? 'none needed — everything is already in INR.'
          : consolidated.rate_table
              .map((use) =>
                use.stale
                  ? `${use.from_ccy}→INR uses the ${monthLabel(use.period_used)} rate (${use.period_used ?? '—'}) — carried forward`
                  : `${use.from_ccy} (${use.period_used ?? '—'})`,
              )
              .join(' · ')}
      </p>
    </section>
  );
}

/* ──────────────────────────────────────────────────────────── countries ── */

const COUNTRY_HEADERS = [
  'Country',
  'Currency',
  'Quotes by stage',
  'Confirmed value',
  'Quote-to-order',
  'In flight',
  'Stuck',
  'Sign-offs waiting',
  'Sign-offs overdue',
] as const;

function stageCounts(block: GroupCountryBlockDto): string {
  return block.pipeline.by_stage.map((entry) => `${quoteStageFacts(entry.stage).label} ${String(entry.count)}`).join(' · ');
}
function confirmedCells(block: GroupCountryBlockDto): readonly { currency: string; grand_total: string }[] {
  return block.pipeline.by_stage.find((entry) => entry.stage === 'confirmed')?.by_currency ?? [];
}
function countryName(code: string): string {
  return isCountryCode(code) ? COUNTRIES[code].name : code;
}

function CountriesTable({ countries, canExport }: { countries: readonly GroupCountryBlockDto[]; canExport: boolean }): React.JSX.Element {
  const csvRows = countries.map((block) => [
    `${countryName(block.country_code)} (${block.country_code})`,
    block.currency,
    stageCounts(block),
    confirmedCells(block).map((cell) => formatMoney(cell.grand_total, cell.currency)).join(' | ') || '—',
    block.pipeline.conversion.rate_pct === null ? '—' : `${String(block.pipeline.conversion.rate_pct)}%`,
    `${String(block.work.in_flight.count)} · ${block.work.in_flight.by_currency.map((cell) => formatMoney(cell.amount, cell.currency)).join(' | ') || '—'}`,
    String(block.work.stuck),
    String(block.signoffs.waiting),
    String(block.signoffs.overdue),
  ]);

  return (
    <section aria-labelledby="countries-title" data-testid="countries" className="flex flex-col gap-3">
      <header className="flex flex-wrap items-baseline justify-between gap-3">
        <h2 id="countries-title" className="text-h3 text-fg">Per country — local currency</h2>
        {canExport ? (
          <button
            type="button"
            onClick={() => {
              downloadCsv('group-countries-local.csv', rowsToCsv(COUNTRY_HEADERS, csvRows));
            }}
            className="flex h-8 items-center gap-1.5 whitespace-nowrap rounded-sm border border-line bg-surface px-2.5 text-body13 font-medium text-fg hover:bg-subtle"
          >
            Export countries (local)
          </button>
        ) : null}
      </header>
      <div className="overflow-x-auto rounded-md border border-line bg-surface">
        <table className="w-full min-w-[1180px] border-collapse text-left">
          <caption className="sr-only">Every active country in its own currency</caption>
          <thead>
            <tr className="h-10 border-b border-line bg-subtle">
              {COUNTRY_HEADERS.map((header) => (
                <th key={header} scope="col" className="px-3 text-th uppercase text-fg">{header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {countries.map((block) => (
              <tr key={block.country_code} data-country={block.country_code} className="crf-register-row border-b border-line last:border-b-0 hover:bg-subtle">
                <td className="px-3 align-middle text-body13 font-medium text-fg">
                  {countryName(block.country_code)} <span className="font-mono text-mono text-fg-secondary">{block.country_code}</span>
                </td>
                <td className="px-3 align-middle font-mono text-mono text-fg">{block.currency}</td>
                <td className="px-3 align-middle text-body13 text-fg">{stageCounts(block)}</td>
                <td className="px-3 align-middle text-body13 text-fg">
                  {confirmedCells(block).length === 0 ? <span className="text-fg-secondary">—</span> : (
                    <div className="flex flex-col">
                      {confirmedCells(block).map((cell) => (
                        <ServerAmount key={cell.currency} value={cell.grand_total} currency={cell.currency} />
                      ))}
                    </div>
                  )}
                </td>
                <td className="tnum px-3 align-middle text-body13 text-fg">
                  {block.pipeline.conversion.rate_pct === null ? '—' : `${String(block.pipeline.conversion.rate_pct)}%`}
                </td>
                <td className="px-3 align-middle text-body13 text-fg">
                  <span className="tnum">{block.work.in_flight.count}</span>
                  {block.work.in_flight.by_currency.length === 0 ? null : (
                    <div className="flex flex-col">
                      {block.work.in_flight.by_currency.map((cell) => (
                        <ServerAmount key={cell.currency} value={cell.amount} currency={cell.currency} />
                      ))}
                    </div>
                  )}
                </td>
                <td className="tnum px-3 align-middle text-body13 text-fg">{block.work.stuck}</td>
                <td className="tnum px-3 align-middle text-body13 text-fg">{block.signoffs.waiting}</td>
                <td className="tnum px-3 align-middle text-body13 text-fg">{block.signoffs.overdue}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
